import {
	DEFAULT_EMAIL_DELIVERABILITY_SETTINGS,
	RiskyEmailPolicy,
	riskyEmailPolicySchema,
	LastEmailOutcome,
} from "@signalist/shared-types";
import { z } from "zod";

import { CampaignStatus } from "@/enum/campaign-status.enum";
import { AssignStatus } from "@/enum/list.enum";

import {
	CompanyCategoryResults,
	People,
	PersonaCategoryResults,
} from "../../Apollo/schema/get-people";

export enum ContactsTab {
	CONTACTS = "CONTACTS",
	COMPANIES = "COMPANIES",
	COMPETITORS = "COMPETITORS",
	BLACKLIST = "BLACKLIST",
}

export interface ContactCrmRecord {
	id: number;
	crmType?: string | null;
	currentStatus: string | null;
	lastUpdatedAt: string | null;
	accountOwner: string | null;
	accountOwnerId: string | null;
	url: string | null;
	referenceId: string | null;
	customField1: string | null;
	customField2: string | null;
	syncStatus?: string | null;
	syncError?: string | null;
	hubspotSyncStatus: string | null;
	hubspotSyncError: string | null;
}

export interface ContactProspectOrganization {
	id: number;
	name: string | null;
	/** Email-form company name; null until resolved, so fall back to `name` (PREC-2748). */
	nameNormalized?: string | null;
	city: string | null;
	state: string | null;
	country: string | null;
	industry: string | null;
	logoUrl: string | null;
	websiteUrl: string | null;
	linkedinUrl: string | null;
	foundedYear: number | null;
	estimatedNumEmployees: number | null;
	employeesFrom?: number | null;
	employeesTo?: number | null;
	primaryDomain: string | null;
	phone: string | null;
	email: string | null;
	shortDescription: string | null;
	createdAt: string | null;
	updatedAt: string | null;
	crmOrganizations?: ContactCrmRecord[];
	scoreCompanyCategoryResult?: CompanyCategoryResults | null;
}

export interface ContactProspectEmployment {
	id: number;
	organizationName: string;
	title: string;
	startedAt: string | null;
	current?: boolean | null;
}

export interface ContactListInfo {
	id: number;
	name: string;
	sourceType?: string;
	campaignStatus?: CampaignStatus;
}

export interface SentimentConversationDTO {
	id: number;
	type: string;
	sentiment: string;
	message: string | null;
	sentAt: string;
	subject: string | null;
	senderEmail: string | null;
	receiverEmail: string | null;
	senderLinkedinUrl: string | null;
	receiverLinkedinUrl: string | null;
	messageType: string;
	listPersonId: number | null;
}

export interface ContactListPerson {
	id: number;
	listId?: number;
	assignStatus?: AssignStatus;
	createdAt?: string;
	updatedAt: string;
}

export interface ContactTrackerPerson {
	id: number;
	trackerId: number;
	tracker: {
		id: number;
		name: string;
		list: {
			id: number;
			name: string;
		};
	};
}

export interface ContactProspect {
	id: number;
	name: string | null;
	firstName: string | null;
	middleName?: string | null;
	lastName: string | null;
	email: string | null;
	emailStatus: string | null;
	// Where the address came from, its last verification and the last email sent (PREC-4262).
	emailSource?: string | null;
	emailSetAt?: string | null;
	emailVerifiedAt?: string | null;
	emailVerificationResult?: string | null;
	lastEmailOutcome?: LastEmailOutcome | null;
	title: string | null;
	seniority: string | null;
	linkedinUrl: string | null;
	city: string | null;
	state: string | null;
	country: string | null;
	image: string | null;
	phone: string | null;
	phoneStatus: string | null;
	bookingLink?: string | null;
	headline: string | null;
	about?: string | null;
	language?: string | null;
	connections?: number | null;
	followers?: number | null;
	startedCurrentPosition?: string | null;
	jobChangeOrPromotion?: string | null;
	sentiment: string | null;
	gender: string | null;
	genderProbability: number | null;
	createdAt: string | null;
	updatedAt: string | null;
	prospectEmployments?: ContactProspectEmployment[] | null;
	organization: ContactProspectOrganization | null;
	crmProspects?: ContactCrmRecord[];
	scorePersonaCategoryResult?: PersonaCategoryResults | null;
	lists?: ContactListInfo[];
	listPersons?: ContactListPerson[];
	firstSentimentConversation?: SentimentConversationDTO | null;
	bestSentimentConversation?: SentimentConversationDTO | null;
	lastSentimentConversation?: SentimentConversationDTO | null;
	trackerPersons?: ContactTrackerPerson[];
	lastContactAt?: string | null;
	lastResponseAt?: string | null;
	deepResearchResponse?: People["deepResearchResponse"];
}

export interface ContactOrganization {
	id: number;
	name: string;
	industry: string | null;
	city: string | null;
	state: string | null;
	country: string | null;
	estimatedNumEmployees: number | null;
	websiteUrl: string | null;
	primaryDomain: string | null;
	linkedinUrl: string | null;
	logoUrl: string | null;
	foundedYear: number | null;
	email: string | null;
	phone: string | null;
	rawAddress: string | null;
	shortDescription: string | null;
	employeesFrom: number | null;
	employeesTo: number | null;
	crmOrganizations?: Array<{
		id: number;
		crmType?: string | null;
		url: string | null;
		accountOwner: string | null;
		accountOwnerId: string | null;
		referenceId: string | null;
		lastUpdatedAt: string | null;
		customField1: string | null;
		customField2: string | null;
		syncStatus?: string | null;
		syncError?: string | null;
	}>;
}

export interface ContactCompetitorOrganization {
	id: number;
	name: string;
	organization: ContactOrganization;
	prospects: ContactProspect[];
}

export interface ContactsRequestParams {
	page: number;
	limit: number;
	searchValue?: string;
	sortBy?: string;
	sortOrder?: string;
	organizationId?: number;
	filter?: string;
	semanticSearchProspects?: string;
	semanticSearchOrganizations?: string;
}

export enum FROM_TYPE {
	CONTACTS = "CONTACTS",
	LIST = "LIST",
}

export interface UpdateContactProspectScoring {
	prospectId: number;
	overallScore?: string | null;
	scorePersonaCategoryId?: number | null;
	personaResult?: string | null;
	scoreCompanyCategoryId?: number | null;
	companyResult?: string | null;
	reason?: string;
	fromType: FROM_TYPE.CONTACTS;
}

export interface UpdateContactProspect {
	id: number;
	name?: string;
	firstName?: string;
	middleName?: string;
	lastName?: string;
	email?: string;
	phone?: string;
	linkedinUrl?: string;
	gender?: string;
	bookingLink?: string;
}

export interface UpdateContactOrganization {
	id: number;
	name?: string;
}

export interface ContactSearchRequest {
	ids: number[];
}

export enum ContactSelectType {
	CURRENT_PAGE = "CURRENT_PAGE",
	ALL = "ALL",
}

export interface ContactAddToListRequest {
	selectType: ContactSelectType;
	ids?: number[];
	filter?: string;
	searchValue?: string;
	semanticSearchProspects?: string;
	semanticSearchOrganizations?: string;
	listId?: number;
	listName?: string;
}

export interface ContactAddToTrackerRequest {
	selectType: ContactSelectType;
	ids?: number[];
	filter?: string;
	searchValue?: string;
	semanticSearchProspects?: string;
	semanticSearchOrganizations?: string;
	listId: number;
	addOrganizationOnly?: boolean;
}

export interface ContactAddToTrackerResponse {
	success: boolean;
	added: number;
	skipped: number;
}

export interface OrganizationAddToTrackerRequest {
	selectType: ContactSelectType;
	ids?: number[];
	filter?: string;
	searchValue?: string;
	semanticSearchOrganizations?: string;
	listId: number;
}

export interface OrganizationAddToTrackerResponse {
	success: boolean;
	added: number;
	skipped: number;
}

export interface ContactExportRequest {
	selectType: ContactSelectType;
	ids?: number[];
	filter?: string;
	searchValue?: string;
	semanticSearchProspects?: string;
	semanticSearchOrganizations?: string;
}

export interface ContactListActionResponse {
	success: boolean;
	listId: number;
	listName: string;
	count: number; /** People the selection resolved to that the list already held (PREC-4595). */
	skipped?: number;
}

export interface PaginatedResponse<T> {
	data: T[];
	meta: {
		page: number;
		limit: number;
		itemCount: number;
		pageCount: number;
		hasPreviousPage: boolean;
		hasNextPage: boolean;
	};
}

// Organization-module types live in a sibling file to keep this file under the size ratchet.
export * from "./contact-organization.types";

// ── Blacklisted prospect types ──────────────────────────────────────────────

export interface ContactBlacklistedProspect extends ContactProspect {
	/** PERSON/ORGANIZATION for a blacklist entry, LIST when only a list excluded the lead. */
	blacklistSource: "PERSON" | "ORGANIZATION" | "LIST" | null;
	/** Id of the underlying blacklist entry, when the row is backed by one. */
	blacklistEntryId: number | null;
	/**
	 * Email/domain/LinkedIn URL the entry was created from. Set when no contact matches the entry
	 * yet — the row has no contact record behind it and is displayed under this instead.
	 */
	blacklistIdentifier: string | null;
	blacklistOrigin:
		| "UNSUBSCRIBED_BY_LEAD"
		| "BLACKLISTED_BY_USER"
		| "IMPORTED_BY_USER"
		| "COMPETITOR"
		| null;
	blacklistOriginDetail: string | null;
	/**
	 * Who decided the row belongs here (PREC-3594). Not the same question as `blacklistSource`,
	 * which says *what* is blocked — a competitor is the only origin that can be either, since a
	 * user can mark a company as one by hand.
	 */
	blacklistDetectedBy: "AI" | "MANUAL" | "LEAD" | null;
	/** Reasoning shown on hover. Only a competitor carries one. */
	blacklistReason: string | null;
	/** Display name of whoever added the row, resolved from the entry's creator. */
	blacklistAddedBy: string | null;
	addedToBlacklistAt: string | null;
	snoozed: boolean;
	snoozedUntil: string | null;
}

export interface BlacklistedContactsRequestParams {
	page: number;
	limit: number;
	searchValue?: string;
	sortBy?: string;
	sortOrder?: string;
	filter?: string;
}

export interface ContactAddToBlacklistRequest {
	selectType: ContactSelectType;
	ids?: number[];
	filter?: string;
	searchValue?: string;
	semanticSearchProspects?: string;
	semanticSearchOrganizations?: string;
	addOrganization?: boolean;
}

export interface ContactBlacklistRemoveRequest {
	selectType: ContactSelectType;
	ids?: number[];
	filter?: string;
	searchValue?: string;
	removeOrganization?: boolean;
}

// Competitors-tab types live in a sibling file to keep this file under the size ratchet.
export * from "./contact-competitor.types";

export interface ContactSnoozeRequest {
	selectType: ContactSelectType;
	ids?: number[];
	filter?: string;
	searchValue?: string;
	snoozedUntil: string;
}

export interface ContactUnsnoozeRequest {
	selectType: ContactSelectType;
	ids?: number[];
	filter?: string;
	searchValue?: string;
}

export interface ContactBlacklistActionResponse {
	success: boolean;
	count: number;
	/** Rows a removal left on the blacklist because they are unsubscribes (PREC-2548). */
	skipped?: { name: string }[];
}

export interface ContactBlacklistImpactResponse {
	onListCount: number;
	inActiveCampaignCount: number;
	affectedListNames: string[];
	/** The first companies of the selection, which "also blacklist the company" names (PREC-4425). */
	companies: { name: string | null; domain: string | null; linkedinCompanyUrl: string | null }[];
	companyCount: number;
}

// ── Contact Settings types ──────────────────────────────────────────────

const defaultDeliverability = DEFAULT_EMAIL_DELIVERABILITY_SETTINGS;

export const ContactSettingsSchema = z.object({
	autoArchiveEnabled: z.boolean(),
	autoArchiveMonths: z.number().nullable(),
	autoReEnrichEnabled: z.boolean(),
	emailVerificationEnabled: z.boolean().default(defaultDeliverability.emailVerificationEnabled),
	riskyEmailPolicy: riskyEmailPolicySchema.default(defaultDeliverability.riskyEmailPolicy),
	riskyEmailPauseAbove4Percent: z
		.boolean()
		.default(defaultDeliverability.riskyEmailPauseAbove4Percent),
	bounceRepairEnabled: z.boolean().default(defaultDeliverability.bounceRepairEnabled),
	bounceRepairReviewOnCompanyChange: z
		.boolean()
		.default(defaultDeliverability.bounceRepairReviewOnCompanyChange),
});

export type ContactSettings = z.infer<typeof ContactSettingsSchema>;

export interface UpdateContactSettings {
	autoArchiveEnabled?: boolean;
	autoArchiveMonths?: number | null;
	autoReEnrichEnabled?: boolean;
	emailVerificationEnabled?: boolean;
	riskyEmailPolicy?: RiskyEmailPolicy;
	riskyEmailPauseAbove4Percent?: boolean;
	bounceRepairEnabled?: boolean;
	bounceRepairReviewOnCompanyChange?: boolean;
}
